import * as log from '@sb/log';
import type { Pose } from '@sb/messenger/auto_generated/messenger/geometry';
import {
  TCErrorCode,
  type EnqueueReply,
  type Primitive,
  type SafetySession,
} from '@sb/messenger/auto_generated/messenger/trajectory_controller_service';
import type { TrajectoryControllerConnector } from '@sb/messenger/connectors/generated';
import { wait } from '@sb/utilities';

const ns = log.namespace('enqueueWithTcPrerequisites');

// SetSafetySession applies on the TC's next 1 kHz tick; wait a few ticks before
// the retry so it observes the applied session. The WorldToBaseUpdate enqueue
// reply resolves once the RT thread has queued it, and it applies at
// activation, so the same settle covers it. Only paid on the recovery path.
const PREREQUISITE_SETTLE_MS = 20;

export interface EnqueueWithTcPrerequisitesOptions {
  enqueueTimeoutMs: number;
  setSafetySessionTimeoutMs: number;
  /** Override the post-push settle delay (tests pass 0). */
  settleMs?: number;
  /** Abort the push/retry recovery if the caller cancelled mid-flight. */
  isCancelled?: () => boolean;
}

/**
 * Where the recovery path reads the current TC prerequisites from.
 * `RoutineContext` satisfies this structurally.
 */
export interface TcPrerequisiteSource {
  getTcSafetySession(): SafetySession | null;
  getTcWorldToBase(): Pose;
}

/**
 * Push the TC prerequisites a NOT_READY rejection points at — the current
 * safety session, then the current world→base pose — and wait the settle
 * window. Each push must succeed before the next step runs. Returns true when
 * both pushes landed (the caller may retry its rejected request once), false
 * when recovery could not complete. Both prerequisites are read lazily from
 * `source` at recovery time.
 *
 * Shared by `enqueueWithTcPrerequisites` (jog enqueues) and
 * `getJogDirectionsWithTC` (the jog-directions feasibility query).
 */
export async function pushTcPrerequisites(
  connector: TrajectoryControllerConnector,
  source: TcPrerequisiteSource,
  options: EnqueueWithTcPrerequisitesOptions,
): Promise<boolean> {
  // Resolved lazily: the session may have synced while the rejected request
  // awaited.
  const safetySession = source.getTcSafetySession();

  if (!safetySession) {
    log.warn(ns`recover`, 'NOT_READY but no safety session available to push');

    return false;
  }

  log.info(ns`recover`, 'NOT_READY; pushing safety session + world_to_base');

  try {
    await connector.SetSafetySession(
      safetySession,
      options.setSafetySessionTimeoutMs,
    );
  } catch (error) {
    log.warn(ns`recover`, 'SetSafetySession during recovery failed', { error });

    return false;
  }

  try {
    const worldToBaseReply = await connector.Enqueue(
      { worldToBaseUpdate: { worldToBase: source.getTcWorldToBase() } },
      options.enqueueTimeoutMs,
    );

    if (!worldToBaseReply.accepted) {
      log.warn(ns`recover`, 'WorldToBaseUpdate during recovery rejected', {
        error: worldToBaseReply.error,
      });

      return false;
    }
  } catch (error) {
    log.warn(ns`recover`, 'WorldToBaseUpdate during recovery failed', {
      error,
    });

    return false;
  }

  await wait(options.settleMs ?? PREREQUISITE_SETTLE_MS);

  return true;
}

/**
 * Enqueue a TC primitive, recovering from missing TC prerequisites on
 * `TC_ERROR_NOT_READY` (e.g. after a TC restart). The wire error does not say
 * which prerequisite was missing, so the recovery pushes the current safety
 * session, then the current world→base pose, then retries the Enqueue once.
 * Any recovery failure surfaces the original rejection.
 */
export async function enqueueWithTcPrerequisites(
  connector: TrajectoryControllerConnector,
  primitive: Primitive,
  source: TcPrerequisiteSource,
  options: EnqueueWithTcPrerequisitesOptions,
): Promise<EnqueueReply> {
  const reply = await connector.Enqueue(primitive, options.enqueueTimeoutMs);

  if (reply.accepted || reply.error !== TCErrorCode.TC_ERROR_NOT_READY) {
    return reply;
  }

  if (options.isCancelled?.()) {
    return reply;
  }

  if (!(await pushTcPrerequisites(connector, source, options))) {
    return reply;
  }

  // Re-check: the operator may have released during the push/settle window.
  if (options.isCancelled?.()) {
    return reply;
  }

  return connector.Enqueue(primitive, options.enqueueTimeoutMs);
}
